export enum MutationProcessState {
  NO_FILE = 'NO_FILE',
  PENDING_VALIDATION = 'PENDING_VALIDATION',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  VALID_MUTATION = 'VALID_MUTATION',
  NO_MUTATION_FOUND = 'NO_MUTATION_FOUND',
  CLINICAL_DIAGNOSIS = 'CLINICAL_DIAGNOSIS',
  MORE_INFO_REQUIRED = 'MORE_INFO_REQUIRED',
  // New specific status codes for enhanced file validation
  MISSING_GENETIC_REPORT = 'missing-genetic-report',
  NO_RETT_DIAGNOSIS = 'no-rett-diagnosis',
  MISSING_MUTATION_IN_REPORT = 'missing-mutation-in-report',
  MISSING_NAME = 'missing-name',
  MISSING_SURNAME = 'missing-surname',
  MISSING_DOB = 'missing-dob',
  CUSTOM_INFO_REQUIRED = 'custom-info-required',
  INFO_MISMATCH_NAME = 'info-mismatch-name',
  INFO_MISMATCH_DOB = 'info-mismatch-dob'
}


export interface MutationStatusResponse {
  state: MutationProcessState;
  validation_status?: string | null; // NEW: Backend FileStatus enum value when state="MORE_INFO_REQUIRED"
  allow_upload: boolean;
  show_instructions: boolean;
  show_status: boolean;
  show_mutation_list: boolean;
  last_file_id: string;
  last_updated_at: string;
  validation_description: string;
//  pending_file_count: number;
}

/**
 * Maps backend validation_status field to frontend MutationProcessState enum
 * Handles both SCREAMING_SNAKE_CASE and kebab-case formats from backend
 * @param response Backend mutation status response
 * @returns Frontend display state for UI rendering
 */
export function getDisplayState(response: MutationStatusResponse): MutationProcessState {
  // If state is MORE_INFO_REQUIRED and validation_status is populated, map to specific status
  if (response.state === MutationProcessState.MORE_INFO_REQUIRED && response.validation_status) {
    // Normalize to uppercase with underscores for consistent lookup (handles both formats)
    const normalizedStatus = response.validation_status.toUpperCase().replace(/-/g, '_');
    
    // If validation_status is the generic "more-info-required", just return the state as-is
    if (normalizedStatus === 'MORE_INFO_REQUIRED') {
      return MutationProcessState.MORE_INFO_REQUIRED;
    }
    
    const statusMap: Record<string, MutationProcessState> = {
      'MISSING_GENETIC_REPORT': MutationProcessState.MISSING_GENETIC_REPORT,
      'NO_RETT_DIAGNOSIS': MutationProcessState.NO_RETT_DIAGNOSIS,
      'MISSING_MUTATION_IN_REPORT': MutationProcessState.MISSING_MUTATION_IN_REPORT,
      'MISSING_NAME': MutationProcessState.MISSING_NAME,
      'MISSING_SURNAME': MutationProcessState.MISSING_SURNAME,
      'MISSING_DOB': MutationProcessState.MISSING_DOB,
      'CUSTOM_INFO_REQUIRED': MutationProcessState.CUSTOM_INFO_REQUIRED,
      'INFO_MISMATCH_NAME': MutationProcessState.INFO_MISMATCH_NAME,
      'INFO_MISMATCH_DOB': MutationProcessState.INFO_MISMATCH_DOB,
      'COMPLETED': MutationProcessState.VALID_MUTATION  // Map 'completed' to VALID_MUTATION
    };
    
    // Return mapped status if known, otherwise fallback to generic MORE_INFO_REQUIRED
    if (normalizedStatus in statusMap) {
      return statusMap[normalizedStatus];
    } else {
      // Unknown validation_status value - log warning and fallback to generic
      console.warn('Unknown validation_status value:', response.validation_status);
      return MutationProcessState.MORE_INFO_REQUIRED;
    }
  }
  
  // For all other states or when validation_status is null, return state as-is
  return response.state;
}

// New interface that mirrors the Python TranscriptMutation model
export interface TranscriptMutation {
  /**
   * Full transcript mutation description (e.g., NM_004992.3:c.916C>T)
   */
  hgvs_transcript_variant: string;
  /**
   * Full protein consequence description (e.g., NP_004983.2:p.Ser306Cys)
   */
  protein_consequence_tlr?: string | null;
  /**
   * Short protein consequence description in SLR format (e.g., NP_004983.1:p.(R306C))
   */
  protein_consequence_slr?: string | null;
}


export interface GenomicCoordinate {
  /**
   * Genome assembly version (e.g., GRCh37 or GRCh38)
   */
  assembly: 'GRCh37' | 'GRCh38';
  /**
   * Canonical genomic coordinate (e.g., NC_000023.11:g.154030912G>A)
   */
  hgvs: string;
  /**
   * Start position of the mutation in the genome (1-based index)
   */
  start: number;
  /**
   * End position of the mutation in the genome (1-based index)
   */
  end: number;
  /**
   * Size of the mutation (e.g., 1 for single nucleotide variants, larger for insertions/deletions)
   */
  size?: number;
}


// New interface that mirrors the Python GeneMutation model
export interface GeneMutation {
  /**
   * TODO: LEGACY: This field will be removed in the future. It is used for legacy purposes.
   * Genome assembly version (e.g., GRCh37 or GRCh38)
   */
  genome_assembly: string;
  /**
   * TODO: LEGACY: This field will be removed in the future. It is used for legacy purposes.
   * Canonical genomic coordinate (e.g., NC_000023.11:g.154030912G>A)
   */
  genomic_coordinate: string;
  /**
   * Genomic coordinates 
   */
  genomic_coordinates: GenomicCoordinate[];
  /**
   * Variant type (e.g., SNV, insertion, deletion)
   */
  variant_type: string;
  /**
   * Primary transcript mutation details (e.g., NM_004992.4)
   */
  primary_transcript: TranscriptMutation;
  /**
   * Secondary transcript mutation details (e.g., NM_001110792.2)
   */
  secondary_transcript: TranscriptMutation | null;
}

// Updated mutation interface to incorporate the new GeneMutation structure
export interface IMutation {
  id: string;
  mutationId: string;
  mutation_data: GeneMutation;
  validationStatus: 'validated' | 'pending' | 'processed' | 'invalid';
}